import { useEffect, useState } from "react";
import { KeyRound, Megaphone, MessagesSquare, User, Users } from "lucide-react";
import type { ResourceDraft } from "../ResourceWizard";
import { normalizeDecimalInput } from "../../../../lib/format";
import { ChoiceCard, FieldLabel } from "./WizardUi";

type Props = {
  draft: ResourceDraft;
  pricingMode?: "paid" | "free";
  onBack: () => void;
  onNext: (patch: Partial<ResourceDraft>) => Promise<void>;
};

export function Step2Url({ draft, pricingMode = "paid", onNext }: Props) {
  const [url, setUrl] = useState(draft.url);
  const [resourceKind, setResourceKind] = useState<"channel" | "chat">(
    draft.type.startsWith("chat_") ? "chat" : "channel",
  );
  const [botCheck, setBotCheck] = useState<"token" | "botmembers">(
    draft.bot_verification_method ??
      (draft.verification_mode === "no_check" ? "token" : "botmembers"),
  );
  const [botAction, setBotAction] = useState<"start" | "unique_start">(
    draft.bot_launch_mode ?? "start",
  );
  const [joinMode, setJoinMode] = useState<"plain" | "requests">(
    draft.target_action === "join_request" ? "requests" : "plain",
  );
  const [price, setPrice] = useState(
    normalizeDecimalInput(draft.price_per_action),
  );
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    setUrl(draft.url);
    setResourceKind(draft.type.startsWith("chat_") ? "chat" : "channel");
    setBotCheck(
      draft.bot_verification_method ??
        (draft.verification_mode === "no_check" ? "token" : "botmembers"),
    );
    setBotAction(draft.bot_launch_mode ?? "start");
    setJoinMode(draft.target_action === "join_request" ? "requests" : "plain");
    setPrice(normalizeDecimalInput(draft.price_per_action));
    setErrorMessage(null);
  }, [
    draft.bot_launch_mode,
    draft.bot_verification_method,
    draft.price_per_action,
    draft.target_action,
    draft.type,
    draft.url,
    draft.verification_mode,
  ]);

  const normalizedUrl = url.trim();
  const normalizedPrice = price.replace(",", ".").trim();
  const isBot = draft.type === "bot";
  const isNoCheck = draft.verification_mode === "no_check" && !isBot;
  const requiresPrice = pricingMode === "paid";
  const validUrl = isBot
    ? isTelegramUsernameLink(normalizedUrl)
    : isNoCheck
      ? /^https?:\/\/\S+$/i.test(normalizedUrl)
      : isTelegramResourceLink(normalizedUrl);
  const validPrice =
    !requiresPrice ||
    (Number.isFinite(Number(normalizedPrice)) && Number(normalizedPrice) > 0);
  const paidFields = (holdEnabled: boolean): Partial<ResourceDraft> =>
    requiresPrice
      ? {
          price_per_action: normalizedPrice,
          hold_enabled: holdEnabled,
        }
      : {};

  const onClickNext = () => {
    setErrorMessage(null);
    if (!validUrl) {
      setErrorMessage(
        isNoCheck
          ? "укажите корректную ссылку"
          : "ожидается ссылка вида https://t.me/name, @name, https://t.me/+... или id вида -100...",
      );
      return;
    }
    if (requiresPrice && !validPrice) {
      setErrorMessage("укажите стоимость действия");
      return;
    }
    if (isBot) {
      const username = extractTelegramUsername(normalizedUrl);
      if (!username) {
        setErrorMessage(
          "ожидается ссылка вида https://t.me/<username> или @username",
        );
        return;
      }
      onNext({
        url: normalizedUrl,
        type: "bot",
        verification_mode: "verified",
        target_action: "start_bot",
        bot_launch_mode: botAction,
        bot_verification_method: botCheck,
        botstat_bot_username: username,
        ...paidFields(true),
      });
      return;
    }
    if (isNoCheck) {
      onNext({
        url: normalizedUrl,
        verification_mode: "no_check",
        target_action: "subscribe",
        ...paidFields(false),
      });
      return;
    }
    const isPrivate = isPrivateTelegramResourceInput(normalizedUrl);
    onNext({
      url: normalizedUrl,
      type: resourceTypeForKind(resourceKind, normalizedUrl),
      verification_mode: "verified",
      target_action:
        isPrivate && joinMode === "requests" ? "join_request" : "subscribe",
      ...paidFields(true),
    });
  };

  if (isBot) {
    return (
      <div className="tp-resource-wizard-step space-y-3">
        <div className="space-y-2">
          <FieldLabel>укажите ссылку на ресурс</FieldLabel>
          <input
            className="tp-input"
            placeholder="https://t.me/bot или @bot"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </div>

        <div className="space-y-2">
          <FieldLabel>способ запуска</FieldLabel>
          <div className="grid gap-2">
            <ChoiceCard
              label="запуск /start"
              icon={User}
              active={botAction === "start"}
              onClick={() => setBotAction("start")}
            />
            <ChoiceCard
              label="запуск /start уникального пользователя"
              icon={UserStarIcon}
              active={botAction === "unique_start"}
              onClick={() => setBotAction("unique_start")}
            />
          </div>
        </div>

        <div className="space-y-2">
          <FieldLabel>способ проверки запуска бота</FieldLabel>
          <div className="grid gap-2">
            <ChoiceCard
              label="через токен бота"
              icon={KeyRound}
              active={botCheck === "token"}
              onClick={() => setBotCheck("token")}
            />
            <ChoiceCard
              label="через сервис botmembers"
              icon={Users}
              active={botCheck === "botmembers"}
              onClick={() => setBotCheck("botmembers")}
            />
          </div>
        </div>

        {requiresPrice ? (
          <input
            className="tp-input"
            placeholder="стоимость действия, ₽"
            value={price}
            onChange={(e) => setPrice(e.target.value.replace(",", "."))}
            inputMode="decimal"
          />
        ) : null}
        {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
        <button
          disabled={!validUrl || !validPrice}
          className="tp-button-primary w-full disabled:opacity-50"
          onClick={onClickNext}
        >
          далее
        </button>
      </div>
    );
  }

  if (isNoCheck) {
    return (
      <div className="tp-resource-wizard-step space-y-3">
        <div className="space-y-2">
          <FieldLabel>укажите ссылку на ресурс</FieldLabel>
          <input
            className="tp-input"
            placeholder="https://example.com/page"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
        </div>
        {requiresPrice ? (
          <div className="tp-card-soft text-sm text-muted-foreground">
            внимание: оплата будет идти на основе предположений по схожему
            ресурсу
          </div>
        ) : null}
        {requiresPrice ? (
          <input
            className="tp-input"
            placeholder="стоимость действия, ₽"
            value={price}
            onChange={(e) => setPrice(e.target.value.replace(",", "."))}
            inputMode="decimal"
          />
        ) : null}
        {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
        <button
          disabled={!validUrl || !validPrice}
          className="tp-button-primary w-full disabled:opacity-50"
          onClick={onClickNext}
        >
          далее
        </button>
      </div>
    );
  }

  return (
    <div className="tp-resource-wizard-step space-y-3">
      <div className="space-y-2">
        <FieldLabel>укажите ссылку или id</FieldLabel>
        <input
          className="tp-input"
          placeholder="https://t.me/… или @channel или id -100..."
          value={url}
          onChange={(e) => setUrl(e.target.value)}
        />
      </div>

      <div className="space-y-2">
        <FieldLabel>тип ресурса</FieldLabel>
        <div className="grid gap-2">
          <ChoiceCard
            label="канал"
            icon={Megaphone}
            active={resourceKind === "channel"}
            onClick={() => setResourceKind("channel")}
          />
          <ChoiceCard
            label="чат"
            icon={MessagesSquare}
            active={resourceKind === "chat"}
            onClick={() => setResourceKind("chat")}
          />
        </div>
      </div>

      {isPrivateTelegramResourceInput(normalizedUrl) ? (
        <div className="space-y-2">
          <FieldLabel>тип ссылки</FieldLabel>
          <div className="grid gap-2">
            <ChoiceCard
              label="ссылка без заявок"
              icon={TablerLinkOffIcon}
              active={joinMode === "plain"}
              onClick={() => setJoinMode("plain")}
            />
            <ChoiceCard
              label="ссылка с заявками"
              icon={TablerLinkIcon}
              active={joinMode === "requests"}
              onClick={() => setJoinMode("requests")}
            />
          </div>
        </div>
      ) : null}

      {requiresPrice ? (
        <input
          className="tp-input"
          placeholder="стоимость действия, ₽"
          value={price}
          onChange={(e) => setPrice(e.target.value.replace(",", "."))}
          inputMode="decimal"
        />
      ) : null}
      {errorMessage && <p className="text-sm text-red-600">{errorMessage}</p>}
      <button
        disabled={!validUrl || !validPrice}
        className="tp-button-primary w-full disabled:opacity-50"
        onClick={onClickNext}
      >
        далее
      </button>
    </div>
  );
}

function isTelegramUsernameLink(value: string): boolean {
  return (
    /^https?:\/\/(?:t\.me|telegram\.me)\/[A-Za-z0-9_][A-Za-z0-9_/?=&-]*$/i.test(
      value,
    ) || /^@[A-Za-z0-9_]+$/.test(value)
  );
}

function isTelegramInviteLink(value: string): boolean {
  return /^https?:\/\/(?:t\.me|telegram\.me)\/(?:\+|joinchat\/)[^\s/?#]+\/?$/i.test(
    value,
  );
}

function isTelegramChatId(value: string): boolean {
  return /^-\d{5,}$/.test(value);
}

function isPrivateTelegramResourceInput(value: string): boolean {
  return isTelegramInviteLink(value) || isTelegramChatId(value);
}

function isTelegramResourceLink(value: string): boolean {
  return (
    isTelegramUsernameLink(value) ||
    isTelegramInviteLink(value) ||
    isTelegramChatId(value)
  );
}

function resourceTypeForKind(
  kind: "channel" | "chat",
  value: string,
): ResourceDraft["type"] {
  if (kind === "chat") {
    return isPrivateTelegramResourceInput(value)
      ? "chat_private"
      : "chat_public";
  }
  return isPrivateTelegramResourceInput(value)
    ? "channel_private"
    : "channel_public";
}

function extractTelegramUsername(value: string): string | null {
  const httpsMatch = value.match(
    /^https?:\/\/(?:t\.me|telegram\.me)\/([A-Za-z0-9_]+)\/?(?:\?.*)?$/i,
  );
  if (httpsMatch) return httpsMatch[1];
  if (value.startsWith("@") && /^@[A-Za-z0-9_]+$/.test(value))
    return value.slice(1);
  return null;
}

function UserStarIcon({
  className,
  "aria-hidden": ariaHidden,
}: {
  className?: string;
  "aria-hidden"?: boolean;
}) {
  return (
    <svg
      className={className}
      aria-hidden={ariaHidden}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16.051 12.616a1 1 0 0 1 1.909.024l.737 1.452a1 1 0 0 0 .737.535l1.634.256a1 1 0 0 1 .588 1.806l-1.172 1.168a1 1 0 0 0-.282.866l.259 1.613a1 1 0 0 1-1.541 1.134l-1.465-.75a1 1 0 0 0-.912 0l-1.465.75a1 1 0 0 1-1.539-1.133l.258-1.613a1 1 0 0 0-.282-.866l-1.156-1.153a1 1 0 0 1 .572-1.822l1.633-.256a1 1 0 0 0 .737-.535z" />
      <path d="M8 15H7a4 4 0 0 0-4 4v2" />
      <circle cx="10" cy="7" r="4" />
    </svg>
  );
}

function TablerLinkIcon({
  className,
  "aria-hidden": ariaHidden,
}: {
  className?: string;
  "aria-hidden"?: boolean;
}) {
  return (
    <svg
      className={className}
      aria-hidden={ariaHidden}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 15l6-6" />
      <path d="m11 6 .463-.536a5 5 0 0 1 7.071 7.072L18 13" />
      <path d="m13 18-.397.534a5.068 5.068 0 0 1-7.127 0 4.972 4.972 0 0 1 0-7.071L6 11" />
    </svg>
  );
}

function TablerLinkOffIcon({
  className,
  "aria-hidden": ariaHidden,
}: {
  className?: string;
  "aria-hidden"?: boolean;
}) {
  return (
    <svg
      className={className}
      aria-hidden={ariaHidden}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path stroke="none" d="M0 0h24v24H0z" fill="none" />
      <path d="M9 15l3 -3m2 -2l1 -1" />
      <path d="M11 6l.463 -.536a5 5 0 0 1 7.071 7.072l-.534 .464" />
      <path d="M3 3l18 18" />
      <path d="M13 18l-.397 .534a5.068 5.068 0 0 1 -7.127 0a4.972 4.972 0 0 1 0 -7.071l.524 -.463" />
    </svg>
  );
}
